import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { getBuildingLightDevices } from 'src/config/building-lighting-config';

export interface DeviceRegistry {
  lights: string[];
  climate: string[];
  lastUnitId?: string;
  buildingId?: string;
  bedroomCount?: number;
  bathroomCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceRegistryService {
  private readonly STORAGE_KEY = 'device_registry';
  
  // Hardcoded devices that should always be available
  // Note: These are the device IDs without the entity type prefix
  private readonly HARDCODED_LIGHTS = [
    'livingspace_a', 
    'hallway_a',
    'bedroom_a'
  ];
  
  private readonly HARDCODED_CLIMATE = [
    'livingspace_a',
    'bedroom_a'
  ];
  
  // Full entity IDs for MQTT requests
  private readonly HARDCODED_LIGHT_ENTITIES = [
    'light.all_lights',
    'light.livingspace_a_lights', 
    'light.hallway_a_lights',
    'light.bedroom_a_lights'
  ];
  
  private readonly HARDCODED_CLIMATE_ENTITIES = [
    'climate.livingspace_a',
    'climate.bedroom_a'
  ];
  
  private registry$ = new BehaviorSubject<DeviceRegistry>({
    lights: [...this.HARDCODED_LIGHTS],
    climate: [...this.HARDCODED_CLIMATE]
  });

  constructor() {
    this.loadRegistry();
  }

  /**
   * Get observable of current device registry
   */
  public getRegistry(): Observable<DeviceRegistry> {
    return this.registry$.asObservable();
  }

  /**
   * Get current registry value
   */
  public getCurrentRegistry(): DeviceRegistry {
    return this.registry$.value;
  }

  /**
   * Update unit information and adjust device expectations based on building
   */
  public async updateUnitInfo(
    unitId: string,
    buildingId?: string,
    bedroomCount?: number,
    bathroomCount?: number
  ): Promise<void> {
    const current = this.registry$.value;

    // Get the correct light devices for this building
    const lightDevices = buildingId ? getBuildingLightDevices(buildingId) : [...this.HARDCODED_LIGHTS];

    // If unit changed, we need to reset device lists
    if (current.lastUnitId && current.lastUnitId !== unitId) {
      // console.log('Unit changed from', current.lastUnitId, 'to', unitId, '- resetting device registry for building', buildingId);
      this.registry$.next({
        lights: lightDevices,
        climate: [...this.HARDCODED_CLIMATE],
        lastUnitId: unitId,
        buildingId: buildingId,
        bedroomCount: bedroomCount || 1,
        bathroomCount: bathroomCount || 1
      });
    } else {
      // Just update counts if they've changed
      this.registry$.next({
        ...current,
        lights: lightDevices,
        lastUnitId: unitId,
        buildingId: buildingId,
        bedroomCount: bedroomCount || current.bedroomCount || 1,
        bathroomCount: bathroomCount || current.bathroomCount || 1
      });
    }

    await this.saveRegistry();
  }

  /**
   * Register discovered devices from MQTT
   * Only adds new devices, doesn't remove existing ones
   */
  public async registerDevices(type: 'lights' | 'climate', deviceIds: string[]): Promise<void> {
    const current = this.registry$.value;
    const existing = new Set(current[type]);
    
    // Add any new devices
    let hasChanges = false;
    for (const deviceId of deviceIds) {
      if (!existing.has(deviceId)) {
        existing.add(deviceId);
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      this.registry$.next({
        ...current,
        [type]: Array.from(existing)
      });
      await this.saveRegistry();
    }
  }

  /**
   * Get expected device list based on unit configuration
   * This ensures we show the right number of devices even if some aren't discovered yet
   */
  public getExpectedDevices(type: 'lights' | 'climate'): string[] {
    // Always return the hardcoded devices
    if (type === 'lights') {
      return [...this.HARDCODED_LIGHTS];
    } else {
      return [...this.HARDCODED_CLIMATE];
    }
  }
  
  /**
   * Get hardcoded device IDs for a specific type
   */
  public getHardcodedDevices(type: 'lights' | 'climate'): string[] {
    if (type === 'lights') {
      return [...this.HARDCODED_LIGHTS];
    } else {
      return [...this.HARDCODED_CLIMATE];
    }
  }
  
  /**
   * Get hardcoded full entity IDs for MQTT
   */
  public getHardcodedEntityIds(type: 'lights' | 'climate'): string[] {
    if (type === 'lights') {
      return [...this.HARDCODED_LIGHT_ENTITIES];
    } else {
      return [...this.HARDCODED_CLIMATE_ENTITIES];
    }
  }

  private getExpectedLightCount(bedroomCount: number): number {
    // 1 bedroom: 3 zones (living, bedroom, hallway/bathroom)
    // 2 bedrooms: 6 zones (living, 2 bedrooms, 2 bathrooms, hallway)
    // 3 bedrooms: 9 zones (living, 3 bedrooms, 3 bathrooms, hallway, dining)
    // Always show at least 3 zones
    return Math.max(3, bedroomCount * 3);
  }

  private async loadRegistry(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: this.STORAGE_KEY });
      if (value) {
        const registry = JSON.parse(value) as DeviceRegistry;
        // Ensure hardcoded devices are always included
        const lightsSet = new Set([...this.HARDCODED_LIGHTS, ...(registry.lights || [])]);
        const climateSet = new Set([...this.HARDCODED_CLIMATE, ...(registry.climate || [])]);
        
        this.registry$.next({
          ...registry,
          lights: Array.from(lightsSet),
          climate: Array.from(climateSet)
        });
      }
    } catch (error) {
      console.error('Error loading device registry:', error);
    }
  }

  private async saveRegistry(): Promise<void> {
    try {
      await Preferences.set({
        key: this.STORAGE_KEY,
        value: JSON.stringify(this.registry$.value)
      });
    } catch (error) {
      console.error('Error saving device registry:', error);
    }
  }

  /**
   * Clear registry (for logout)
   */
  public async clearRegistry(): Promise<void> {
    this.registry$.next({
      lights: [],
      climate: []
    });
    await Preferences.remove({ key: this.STORAGE_KEY });
  }
}